import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MagnifyingGlass, FunnelSimple, X } from "@phosphor-icons/react";
import { api, formatErr } from "@/lib/api";
import { saveBrowseContext } from "@/lib/browseContext";
import { readBrowseReturn, clearBrowseReturn } from "@/lib/browsePosition";
import MobileLegend from "@/components/MobileLegend";
import SplashMessageBox from "@/components/SplashMessageBox";
import { PremiumRail } from "@/components/SponsorsSidebar";
import BrowseSidebarFilters from "@/components/browse/BrowseSidebarFilters";
import BrowseFilterDrawer from "@/components/browse/BrowseFilterDrawer";
import BrowseFeaturedStrip from "@/components/browse/BrowseFeaturedStrip";
import NeedleContestBanner from "@/components/browse/NeedleContestBanner";
import BrowseListingsGrid from "@/components/browse/BrowseListingsGrid";

const PAGE_SIZE = 60;

const EMPTY_FILTERS = {
  q: "", sport: "", team: "", brand: "", year: "", seller: "", price_min: "", price_max: "",
  base: false, insert: false, variation: false,
  rookie: false, autograph: false, relic: false, numbered: false, ssp: false, sp: false, graded: false,
  accepts_offers: false, sort: "newest",
};

// URL <-> filter-state helpers. Filters live in the URL so
//   (a) "View all Football" produces a shareable `/browse?sport=Football` URL, and
//   (b) Back-to-Browse from a listing lands the user on the same filtered view.
function filtersFromParams(params) {
  const f = { ...EMPTY_FILTERS };
  for (const [k, defaultVal] of Object.entries(EMPTY_FILTERS)) {
    if (!params.has(k)) continue;
    const raw = params.get(k);
    if (typeof defaultVal === "boolean") f[k] = raw === "true" || raw === "1";
    else f[k] = raw;
  }
  return f;
}

function filtersToParams(f) {
  const params = new URLSearchParams();
  // Iterate EMPTY_FILTERS keys in a fixed order → stable, comparable URL strings.
  for (const [k, defaultVal] of Object.entries(EMPTY_FILTERS)) {
    const v = f[k];
    if (v === defaultVal) continue;
    if (v === "" || v === false || v == null) continue;
    params.set(k, String(v));
  }
  return params;
}

export default function Browse() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [facets, setFacets] = useState({ sports: {}, types: {}, total: 0 });
  const [featured, setFeatured] = useState({ recent: [], most_expensive: null, most_liked: [] });
  const [likedIds, setLikedIds] = useState(() => new Set());
  const [stats, setStats] = useState(null);
  const [recentlySold, setRecentlySold] = useState([]);
  const [contest, setContest] = useState(null);
  // Filters hydrate from the URL on mount so navigating directly to
  // `/browse?sport=Football` (or coming back from a listing) restores the view.
  const [filters, setFiltersRaw] = useState(() => filtersFromParams(searchParams));

  // Wrapper: any filter change also updates the URL query string so it's
  // preserved across the round-trip to ListingDetail. `replace: true`
  // avoids flooding history with an entry per keystroke.
  const setFilters = useCallback(
    (next) => {
      setFiltersRaw((prev) => {
        const resolved = typeof next === "function" ? next(prev) : next;
        setSearchParams(filtersToParams(resolved), { replace: true });
        return resolved;
      });
    },
    [setSearchParams],
  );

  const [totalListings, setTotalListings] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v === "" || v === false || v == null) return;
        params[k] = v;
      });
      // Fresh filter change → reset paging. Grouped default view (no sport
      // filter) fetches a larger first page so all sport sections populate;
      // filtered views use the standard page size.
      params.offset = 0;
      params.limit = filters.sport ? PAGE_SIZE : 250;
      const { data } = await api.get("/listings", { params });
      setListings(data.listings || []);
      setTotalListings(data.total || (data.listings || []).length);
      setHasMore(!!data.has_more);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v === "" || v === false || v == null) return;
        params[k] = v;
      });
      params.offset = listings.length;
      params.limit = PAGE_SIZE;
      const { data } = await api.get("/listings", { params });
      setListings((prev) => [...prev, ...(data.listings || [])]);
      setHasMore(!!data.has_more);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoadingMore(false);
    }
  }, [filters, listings.length, loadingMore, hasMore]);

  useEffect(() => { load(); }, [load]);

  // Restore scroll position when the user returns from a listing detail page.
  // sessionStorage remembers the exact URL + target card id + raw scrollY.
  // We prefer scrolling to the card itself (survives minor list re-ordering);
  // fall back to raw scrollY. If the target card isn't in the current batch
  // yet, `loadMore` is invoked a couple of times before giving up.
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current) return;
    if (loading || !listings.length) return;
    const ret = readBrowseReturn();
    if (!ret) return;
    const currentUrl = `${window.location.pathname}${window.location.search}`;
    if (ret.url !== currentUrl) { clearBrowseReturn(); return; }
    restoredRef.current = true;

    let attempts = 0;
    const flashJustViewed = (el) => {
      // 1.8 s pulse ring on the card the user came from. Class is added,
      // then removed after the animation completes so it can re-trigger
      // on a subsequent round-trip.
      el.classList.add("h57-just-viewed");
      window.setTimeout(() => el.classList.remove("h57-just-viewed"), 1800);
    };
    const tryRestore = () => {
      const el = ret.listingId ? document.querySelector(`[data-testid="listing-card-${ret.listingId}"]`) : null;
      if (el) {
        el.scrollIntoView({ behavior: "instant", block: "center" });
        flashJustViewed(el);
        clearBrowseReturn();
        return;
      }
      // Card not in the DOM yet — load more pages until it appears (max 3 tries).
      if (attempts < 3 && hasMore) {
        attempts += 1;
        loadMore();
        setTimeout(tryRestore, 400);
        return;
      }
      // Give up on the card lookup; use the raw pixel offset as a fallback.
      if (typeof ret.scrollY === "number") {
        window.scrollTo({ top: ret.scrollY, behavior: "instant" });
      }
      clearBrowseReturn();
    };
    // Give the layout one frame to settle before we try.
    requestAnimationFrame(tryRestore);
  }, [loading, listings, hasMore, loadMore]);

  // Featured strip — refresh when the pool size changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/listings/featured");
        if (!cancelled) setFeatured(data || { recent: [], most_expensive: null, most_liked: [] });
      } catch (err) {
        console.error("featured fetch failed", err);
      }
    })();
    return () => { cancelled = true; };
  }, [listings.length]);

  // Hydrate the current user's liked-listing set (public: 401 leaves it empty).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/listings/likes/mine");
        if (!cancelled) setLikedIds(new Set(data.listing_ids || []));
      } catch { /* not signed in → no likes to hydrate */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Community stats — refreshed opportunistically on pool changes + 60 s poll
  // so the "active now" number stays fresh without a page reload.
  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const { data } = await api.get("/community/stats");
        if (!cancelled) setStats(data);
      } catch (err) {
        console.error("community stats fetch failed", err);
      }
    };
    fetchStats();
    const timer = setInterval(fetchStats, 60000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [listings.length]);

  // Recently sold — image-only marquee. Anonymous (no buyer/seller/price).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/listings/recently-sold", { params: { limit: 10 } });
        if (!cancelled) setRecentlySold(data?.items || []);
      } catch (err) {
        console.error("recently-sold fetch failed", err);
      }
    })();
    return () => { cancelled = true; };
  }, [stats?.cards_sold]);

  // Active Hunt-the-Needle contest — shows a promo banner on Browse.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/contests/needle/current");
        if (!cancelled) setContest(data?.contest || null);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Save the visible listing order into browse context so ListingDetail
  // can offer Prev/Next navigation. Order mirrors the rendered grid.
  useEffect(() => {
    if (!listings.length) return;
    if (!filters.sport) {
      const PER_SPORT_CAP = 25;
      const groups = {};
      for (const l of listings) {
        const s = l.sport || "Other";
        (groups[s] = groups[s] || []).push(l);
      }
      const sortedSports = Object.keys(groups).sort((a, b) => groups[b].length - groups[a].length);
      const ordered = [];
      for (const sport of sortedSports) {
        for (const l of groups[sport].slice(0, PER_SPORT_CAP)) ordered.push(l.id);
      }
      saveBrowseContext(ordered, "browse");
    } else {
      saveBrowseContext(listings.map((l) => l.id), `browse:${filters.sport}`);
    }
  }, [listings, filters.sport]);

  // Fetch facet counts (sport + card-type totals across all active listings).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/listings/facets");
        if (!cancelled) setFacets(data || { sports: {}, types: {}, total: 0 });
      } catch {
        /* silent — filter chips will just default to 0 counts */
      }
    })();
    return () => { cancelled = true; };
  }, [listings.length]);

  const resetFilters = () => setFilters(EMPTY_FILTERS);

  const activeFiltersCount = Object.entries(filters).filter(([k, v]) => {
    if (k === "sort") return false;
    return v !== "" && v !== false;
  }).length;

  // Build the year shortcuts strictly from the facet data — most recent first,
  // capped at 6 chips to keep the sidebar tidy. "Vintage <1980" is only added
  // if there is at least one pre-1980 listing available.
  const yearFacets = facets.years || {};
  const availableYears = Object.entries(yearFacets)
    .map(([y, n]) => ({ year: Number(y), count: n }))
    .filter((row) => row.year >= 1980 && row.count > 0)
    .sort((a, b) => b.year - a.year)
    .slice(0, 6);
  const vintageCount = facets.vintage_count || 0;
  const yearChips = [
    ...availableYears.map((row) => ({ label: String(row.year), v: String(row.year), count: row.count })),
    ...(vintageCount > 0 ? [{ label: "Vintage <1980", v: "1979", count: vintageCount }] : []),
  ];

  return (
    <div className="max-w-full px-3 md:px-4 py-4 md:py-8 flex gap-4 xl:gap-6">
      {/* Left sidebar — persistent desktop quick filters */}
      <BrowseSidebarFilters
        filters={filters}
        setFilters={setFilters}
        facets={facets}
        activeFiltersCount={activeFiltersCount}
        resetFilters={resetFilters}
        yearChips={yearChips}
      />

      {/* Main results column */}
      <div className="flex-1 min-w-0">
        {/* Rotating splash announcements — admin-managed (up to 5). Renders
            nothing when there are no active messages. */}
        <SplashMessageBox />

        {/* Search bar */}
        <div className="flex gap-2 items-center mb-4">
          <div className="flex-1 relative">
            <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none z-10" />
            <input
              data-testid="browse-search"
              className="h57-input !pl-10"
              placeholder="Search player, team, brand, set, seller…"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            />
          </div>
          <button
            onClick={() => setShowFilters(true)}
            data-testid="browse-filters-btn"
            className="h57-btn-secondary lg:hidden flex items-center gap-2 relative shrink-0 !py-3 !px-4"
          >
            <FunnelSimple size={16} weight="bold" />
            <span className="hidden sm:inline">Filters</span>
            {activeFiltersCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#FF5F1F] text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full font-mono font-bold">
                {activeFiltersCount}
              </span>
            )}
          </button>
        </div>

        {/* Sort chips */}
        <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1">
          {[
            { v: "newest", label: "Newest" },
            { v: "price_low", label: "Price ↑" },
            { v: "price_high", label: "Price ↓" },
          ].map((s) => (
            <button
              key={s.v}
              data-testid={`sort-${s.v}`}
              onClick={() => setFilters({ ...filters, sort: s.v })}
              className={`h57-chip !py-1.5 !px-3 shrink-0 ${filters.sort === s.v ? "!border-[#FF5F1F] !text-[#FF5F1F]" : ""}`}
            >
              {s.label}
            </button>
          ))}
          {activeFiltersCount > 0 && (
            <button onClick={resetFilters} data-testid="browse-clear-filters" className="h57-chip !py-1.5 !px-3 shrink-0 !border-red-500/30 !text-red-400">
              <X size={12} weight="bold" /> Clear
            </button>
          )}
        </div>

        <MobileLegend />

        {activeFiltersCount === 0 && <NeedleContestBanner contest={contest} />}

        {activeFiltersCount === 0 && (
          <BrowseFeaturedStrip
            featured={featured}
            likedIds={likedIds}
            stats={stats}
            recentlySold={recentlySold}
          />
        )}

        <BrowseListingsGrid
          loading={loading}
          listings={listings}
          filters={filters}
          setFilters={setFilters}
          likedIds={likedIds}
          hasMore={hasMore}
          loadMore={loadMore}
          loadingMore={loadingMore}
          totalListings={totalListings}
          pageSize={PAGE_SIZE}
        />
      </div>
      {/* end main results column */}

      {/* Right rail — Premium ads only. NOT sticky — scrolls with the page. */}
      <div className="hidden lg:block">
        <PremiumRail />
      </div>

      {/* Mobile filter drawer */}
      {showFilters && (
        <BrowseFilterDrawer
          filters={filters}
          setFilters={setFilters}
          resetFilters={resetFilters}
          onClose={() => setShowFilters(false)}
        />
      )}
    </div>
  );
}
